import React, { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useToast } from '../../contexts/ToastContext.jsx';
import { useTheme } from '../../contexts/ThemeContext.jsx';
import { ApiError } from '../../utils/apiClient.js';
import { parseApiError } from '../../utils/consolidatedErrorHandling.js';
import { apiRequest } from '../../utils/apiClient.js';
import UnifiedParrotCreation from '../UnifiedParrotCreation.jsx';

/**
 * Enhanced parrot creation modal for admin-created parrots
 * Integrates the enhanced ParrotCreation component with admin-specific features
 */
const ParrotCreationModal = ({
  isOpen,
  onClose,
  onParrotCreated
}) => {
  const { getToken } = useAuth();
  const { showToast } = useToast();
  const { theme } = useTheme();
  
  // State management
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [useEnhancedCreation, setUseEnhancedCreation] = useState(true);
  const [assignToUser, setAssignToUser] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  
  // Phone number assignment state
  const [availablePhoneNumbers, setAvailablePhoneNumbers] = useState([]);
  const [selectedPhoneNumbers, setSelectedPhoneNumbers] = useState([]);
  const [loadingPhoneNumbers, setLoadingPhoneNumbers] = useState(false);
  const [assignPhoneNumbers, setAssignPhoneNumbers] = useState(false);
  
  // Legacy form data for fallback
  const [formData, setFormData] = useState({
    name: '',
    prompt: '',
    voice_id: '',
    voice_settings: {
      stability: 0.5,
      similarity_boost: 0.5,
      style: 0.0,
      use_speaker_boost: true
    },
    status: 'active',
    user_id: '',
    assign_to_user: false
  });
  const [errors, setErrors] = useState({});

  /**
   * Fetch users for assignment dropdown
   */
  const fetchUsers = async () => {
    try {
      setLoadingUsers(true);
      
      const response = await apiRequest('/api/admin/users?limit=100&sortBy=first_name&sortOrder=asc', {
        headers: {
          'Authorization': `Bearer ${await getToken()}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  /**
   * Fetch available phone numbers for assignment
   */
  const fetchAvailablePhoneNumbers = async () => {
    try {
      setLoadingPhoneNumbers(true);
      
      const response = await apiRequest('/api/admin/phone-numbers/all', {
        headers: {
          'Authorization': `Bearer ${await getToken()}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Filter for numbers that are assigned to users but not to parrots
        const availableForParrotAssignment = (data.phoneNumbers || []).filter(
          number => number.assigned_user_id && !number.assigned_parrot_id
        );
        setAvailablePhoneNumbers(availableForParrotAssignment);
      }
    } catch (error) {
      console.error('Error fetching available phone numbers:', error);
      showToast('Failed to load available phone numbers', 'error');
    } finally {
      setLoadingPhoneNumbers(false);
    }
  };

  /**
   * Handle form input changes for legacy form
   */
  const handleInputChange = (field, value) => {
    if (field.startsWith('voice_settings.')) {
      const voiceField = field.split('.')[1];
      setFormData(prev => ({
        ...prev,
        voice_settings: {
          ...prev.voice_settings,
          [voiceField]: value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  /**
   * Validate legacy form data
   */
  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.prompt.trim()) {
      newErrors.prompt = 'Prompt is required';
    }

    if (formData.assign_to_user && !formData.user_id) {
      newErrors.user_id = 'Please select a user to assign this parrot to';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };





  /**
   * Handle enhanced parrot creation completion
   */
  const handleEnhancedParrotComplete = async (parrotData) => {
    try {
      setLoading(true);
      
      // If user assignment is enabled, update the parrot with user assignment
      if (assignToUser && selectedUserId && parrotData.parrot_id) {
        const token = await getToken();
        
        const response = await fetch(`https://parrotmate.com/api/admin/parrots/${parrotData.parrot_id}`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            user_id: selectedUserId
          })
        });

        if (!response.ok) {
          console.warn('Failed to assign parrot to user, but parrot was created successfully');
        }
      }

      // If phone number assignment is enabled, assign selected phone numbers
      if (assignPhoneNumbers && selectedPhoneNumbers.length > 0 && parrotData.parrot_id) {
        await assignPhoneNumbersToParrot(parrotData.parrot_id, selectedPhoneNumbers);
      }

      // Notify parent component
      onParrotCreated(parrotData.parrot || parrotData);
      handleClose();
      showToast('Parrot created successfully', 'success');

    } catch (error) {
      console.error('Error in enhanced parrot creation:', error);
      showToast(error.message || 'Failed to complete parrot creation', 'error');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Assign phone numbers to the created parrot
   */
  const assignPhoneNumbersToParrot = async (parrotId, phoneNumberIds) => {
    try {
      const token = await getToken();
      const assignmentPromises = phoneNumberIds.map(async (phoneNumberId) => {
        const response = await apiRequest('/api/phone/assign', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            phone_number_id: phoneNumberId,
            parrot_id: parrotId,
            replace_existing: true
          })
        });

        if (!response.ok) {
          const errorMessage = await parseApiError(response);
          throw new ApiError(errorMessage, response.status, response);
        }

        return response.json();
      });

      await Promise.all(assignmentPromises);
      showToast(`${phoneNumberIds.length} phone number(s) assigned to parrot`, 'success');
    } catch (error) {
      console.error('Error assigning phone numbers:', error);
      showToast('Parrot created but some phone number assignments failed', 'warning');
    }
  };

  /**
   * Handle legacy form submission
   */
  const handleLegacySubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      const token = await getToken();
      
      const submitData = {
        ...formData,
        user_id: formData.assign_to_user ? formData.user_id : null
      };
      delete submitData.assign_to_user;

      const response = await fetch('/api/admin/parrots', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submitData)
      });

      if (!response.ok) {
        const errorMessage = await parseApiError(response);
        throw new ApiError(errorMessage, response.status, response);
      }

      const newParrot = await response.json();

      // If phone number assignment is enabled, assign selected phone numbers
      if (assignPhoneNumbers && selectedPhoneNumbers.length > 0 && newParrot.id) {
        await assignPhoneNumbersToParrot(newParrot.id, selectedPhoneNumbers);
      }

      onParrotCreated(newParrot);
      handleClose();
      showToast('Parrot created successfully', 'success');

    } catch (error) {
      console.error('Error creating parrot:', error);
      showToast(error.message || 'Failed to create parrot', 'error');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle modal close
   */
  const handleClose = () => {
    setFormData({
      name: '',
      prompt: '',
      voice_id: '',
      voice_settings: {
        stability: 0.5,
        similarity_boost: 0.5,
        style: 0.0,
        use_speaker_boost: true
      },
      status: 'active',
      user_id: '',
      assign_to_user: false
    });
    setErrors({});
    setUseEnhancedCreation(true);
    setAssignToUser(false);
    setSelectedUserId('');
    setAssignPhoneNumbers(false);
    setSelectedPhoneNumbers([]);
    setAvailablePhoneNumbers([]);
    onClose();
  };

  /**
   * Handle phone number selection
   */
  const handlePhoneNumberSelection = (phoneNumberId, isSelected) => {
    if (isSelected) {
      setSelectedPhoneNumbers(prev => [...prev, phoneNumberId]);
    } else {
      setSelectedPhoneNumbers(prev => prev.filter(id => id !== phoneNumberId));
    }
  };

  // Load users and phone numbers when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchUsers();
      fetchAvailablePhoneNumbers();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`rounded-lg border w-full max-w-4xl max-h-[90vh] overflow-y-auto ${
        theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
      }`}>
        {/* Header */}
        <div className={`px-6 py-4 border-b flex justify-between items-center ${
          theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <div>
            <h3 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Create New Parrot</h3>
            <p className={`text-sm mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              {useEnhancedCreation ? 'Enhanced creation with AI-guided setup' : 'Quick creation with manual configuration'}
            </p>
          </div>
          <div className="flex items-center space-x-4">
            {/* Creation Mode Toggle */}
            <div className="flex items-center space-x-2">
              <span className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Mode:</span>
              <button
                type="button"
                onClick={() => setUseEnhancedCreation(!useEnhancedCreation)}
                className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                  useEnhancedCreation 
                    ? 'bg-teal-600 text-white' 
                    : theme === 'dark'
                      ? 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {useEnhancedCreation ? 'Enhanced' : 'Legacy'}
              </button>
            </div>
            <button
              onClick={handleClose}
              className={`transition-colors ${
                theme === 'dark' 
                  ? 'text-gray-400 hover:text-white' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              ✕
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {useEnhancedCreation ? (
            <div className="space-y-6">
              {/* Enhanced Parrot Creation Component */}
              <div className={`rounded-lg p-6 ${
                theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'
              }`}>
                <UnifiedParrotCreation
                  onComplete={handleEnhancedParrotComplete}
                  onCancel={handleClose}
                  onError={(error) => showToast(error, 'error')}
                  mode="admin"
                  theme={theme}
                  enableProgressSaving={false}
                  showNameInput={true}
                  showVoiceSelection={true}
                  showUserAssignment={true}
                  assignToUser={assignToUser}
                  selectedUserId={selectedUserId}
                  users={users}
                  onUserAssignmentChange={(assign, userId) => {
                    setAssignToUser(assign);
                    setSelectedUserId(userId);
                  }}
                  showPhoneNumberAssignment={true}
                  assignPhoneNumbers={assignPhoneNumbers}
                  selectedPhoneNumbers={selectedPhoneNumbers}
                  availablePhoneNumbers={availablePhoneNumbers}
                  onPhoneNumberAssignmentChange={(assign, phoneNumbers) => {
                    setAssignPhoneNumbers(assign);
                    setSelectedPhoneNumbers(phoneNumbers);
                  }}
                />
              </div>
            </div>
          ) : (
            /* Legacy Form */
            <form onSubmit={handleLegacySubmit} className="space-y-6">
          {/* Basic Information */}
          <div>
            <h4 className={`text-md font-medium mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Basic Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                    errors.name 
                      ? 'border-red-500' 
                      : theme === 'dark' 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                  }`}
                  placeholder="Enter parrot name"
                />
                {errors.name && (
                  <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                )}
              </div>
              
              <div>
                <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  Status
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                    theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="draft">Draft</option>
                </select>
              </div>
            </div>
          </div>

          {/* User Assignment */}
          <div>
            <h4 className={`text-md font-medium mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>User Assignment</h4>
            <div className="space-y-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="assign_to_user"
                  checked={formData.assign_to_user}
                  onChange={(e) => handleInputChange('assign_to_user', e.target.checked)}
                  className={`h-4 w-4 text-teal-600 focus:ring-teal-500 rounded ${
                    theme === 'dark' ? 'border-gray-600 bg-gray-700' : 'border-gray-300 bg-white'
                  }`}
                />
                <label htmlFor="assign_to_user" className={`ml-2 text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  Assign this parrot to a specific user
                </label>
              </div>
              
              {formData.assign_to_user && (
                <div>
                  <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    Select User *
                  </label>
                  <select
                    value={formData.user_id}
                    onChange={(e) => handleInputChange('user_id', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                      errors.user_id 
                        ? 'border-red-500' 
                        : theme === 'dark'
                          ? 'bg-gray-700 border-gray-600 text-white'
                          : 'bg-white border-gray-300 text-gray-900'
                    }`}
                    disabled={loadingUsers}
                  >
                    <option value="">Select a user...</option>
                    {users.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.first_name && user.last_name 
                          ? `${user.first_name} ${user.last_name} (${user.email})`
                          : user.email
                        }
                      </option>
                    ))}
                  </select>
                  {errors.user_id && (
                    <p className="text-red-500 text-sm mt-1">{errors.user_id}</p>
                  )}
                  {loadingUsers && (
                    <p className={`text-sm mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Loading users...</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Phone Number Assignment */}
          <div>
            <h4 className={`text-md font-medium mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Phone Number Assignment</h4>
            <div className="space-y-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="assign_phone_numbers"
                  checked={assignPhoneNumbers}
                  onChange={(e) => setAssignPhoneNumbers(e.target.checked)}
                  className={`h-4 w-4 text-teal-600 focus:ring-teal-500 rounded ${
                    theme === 'dark' ? 'border-gray-600 bg-gray-700' : 'border-gray-300 bg-white'
                  }`}
                />
                <label htmlFor="assign_phone_numbers" className={`ml-2 text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  Assign phone numbers to this parrot
                </label>
              </div>
              
              {assignPhoneNumbers && (
                <div>
                  <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    Select Phone Numbers
                  </label>
                  {loadingPhoneNumbers ? (
                    <div className={`text-center py-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-500 mx-auto mb-2"></div>
                      <p className="text-sm">Loading available phone numbers...</p>
                    </div>
                  ) : availablePhoneNumbers.length === 0 ? (
                    <div className={`text-center py-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                      <div className="mb-2">📞</div>
                      <div className="text-sm">No available phone numbers</div>
                      <div className="text-xs mt-1">All assigned numbers are already assigned to parrots</div>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-48 overflow-y-auto border rounded-lg p-3">
                      {availablePhoneNumbers.map((number) => (
                        <div
                          key={number.id}
                          className={`flex items-center p-2 rounded border transition-colors ${
                            selectedPhoneNumbers.includes(number.id)
                              ? theme === 'dark'
                                ? 'bg-teal-900/20 border-teal-500'
                                : 'bg-teal-50 border-teal-500'
                              : theme === 'dark'
                                ? 'bg-gray-700 border-gray-600 hover:border-gray-500'
                                : 'bg-white border-gray-300 hover:border-gray-400'
                          }`}
                        >
                          <input
                            type="checkbox"
                            id={`phone_${number.id}`}
                            checked={selectedPhoneNumbers.includes(number.id)}
                            onChange={(e) => handlePhoneNumberSelection(number.id, e.target.checked)}
                            className="h-4 w-4 text-teal-600 focus:ring-teal-500 rounded border-gray-300 dark:border-gray-600"
                          />
                          <label htmlFor={`phone_${number.id}`} className="ml-3 flex-1 cursor-pointer">
                            <div className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                              {number.phone_number}
                            </div>
                            <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                              {number.friendly_name}
                            </div>
                            <div className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
                              Assigned to: {number.assigned_user_name}
                            </div>
                          </label>
                        </div>
                      ))}
                    </div>
                  )}
                  {selectedPhoneNumbers.length > 0 && (
                    <div className={`mt-2 text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                      {selectedPhoneNumbers.length} phone number{selectedPhoneNumbers.length !== 1 ? 's' : ''} selected
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Prompt Configuration */}
          <div>
            <h4 className={`text-md font-medium mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Prompt Configuration</h4>
            <div>
              <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                System Prompt *
              </label>
              <textarea
                value={formData.prompt}
                onChange={(e) => handleInputChange('prompt', e.target.value)}
                rows={6}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                  errors.prompt 
                    ? 'border-red-500' 
                    : theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                }`}
                placeholder="Enter the system prompt for this parrot..."
              />
              {errors.prompt && (
                <p className="text-red-500 text-sm mt-1">{errors.prompt}</p>
              )}
            </div>
          </div>

          {/* Voice Settings */}
          <div>
            <h4 className={`text-md font-medium mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Voice Settings</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  Voice ID
                </label>
                <input
                  type="text"
                  value={formData.voice_id}
                  onChange={(e) => handleInputChange('voice_id', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                    theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                  }`}
                  placeholder="ElevenLabs Voice ID (optional)"
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  Speaker Boost
                </label>
                <select
                  value={formData.voice_settings.use_speaker_boost}
                  onChange={(e) => handleInputChange('voice_settings.use_speaker_boost', e.target.value === 'true')}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                    theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                >
                  <option value="true">Enabled</option>
                  <option value="false">Disabled</option>
                </select>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  Stability: {formData.voice_settings.stability}
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={formData.voice_settings.stability}
                  onChange={(e) => handleInputChange('voice_settings.stability', parseFloat(e.target.value))}
                  className="w-full"
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  Similarity Boost: {formData.voice_settings.similarity_boost}
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={formData.voice_settings.similarity_boost}
                  onChange={(e) => handleInputChange('voice_settings.similarity_boost', parseFloat(e.target.value))}
                  className="w-full"
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  Style: {formData.voice_settings.style}
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={formData.voice_settings.style}
                  onChange={(e) => handleInputChange('voice_settings.style', parseFloat(e.target.value))}
                  className="w-full"
                />
              </div>
            </div>
          </div>

              {/* Form Actions */}
              <div className={`flex justify-end space-x-3 pt-6 border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
                <button
                  type="button"
                  onClick={handleClose}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    theme === 'dark'
                      ? 'text-gray-300 bg-gray-700 hover:bg-gray-600'
                      : 'text-gray-700 bg-gray-200 hover:bg-gray-300'
                  }`}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 flex items-center space-x-2 dark:text-white"
                >
                  {loading && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  )}
                  <span>{loading ? 'Creating...' : 'Create Parrot'}</span>
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ParrotCreationModal;
